/**
 * User Metrics filtering and filter-option derivation.
 *
 * Covers three related jobs: applying the master filter set to the parsed rows,
 * deriving the option lists those filters are built from, and the Excel-style
 * cross-filtering that keeps the dropdowns consistent with each other.
 */
import { UserMetricsMasterFilters, UserMetricsParsedData } from '../UserMetricsInterfaces';
import { DashboardFilters } from '../../fintech-ops-dashboard/FinTechOpsDataInterfaces';
import { findCommonElements3, hasCommonElementWithSet } from '../../fintech-ops-dashboard/FinTechOpsUtils';
import { ALL_APPLICATIONS, MANAGER_ROSTER } from '../../FinTechOpsConstants';
import { DAS_FINTECH_ORG_MANAGER_ALIAS } from 'src/constants/OrgConstants';

export const applyFiltersUserMetrics = (
  items: UserMetricsParsedData[],
  filters: UserMetricsMasterFilters
): [UserMetricsParsedData[], UserMetricsParsedData[]] => {
  // Pre-convert filter arrays to Sets for O(1) lookups instead of O(n) .includes()
  const teamSet = new Set(filters.team);
  const applicationTypeSet = new Set(filters.application_type);
  const applicationNameSet = new Set(filters.application_name);
  const userLoginSet = new Set(filters.user_login);
  const monthSet = new Set(filters.month);
  // Hierarchy-overlap filters are Sets too: findCommonElements3 does an inner .includes()
  // per row, so testing a long manager list against each row's hierarchy chain was
  // O(rows × managers × depth). Building the Set once makes it O(rows × depth).
  const managersSet = new Set(filters.managers);
  const userManagerSet = new Set(filters.user_manager);

  const filteredHistoricData: UserMetricsParsedData[] = items.filter((item: UserMetricsParsedData) => {
    return (
      userLoginSet.has(item.user_login) &&
      teamSet.has(item.team_name) &&
      applicationTypeSet.has(item.application_type) &&
      applicationNameSet.has(item.application) &&
      hasCommonElementWithSet(managersSet, item.manager_name_hierarchy_concat) &&
      hasCommonElementWithSet(userManagerSet, item.user_manager_name_hierarchy)
    );
  });

  const filteredRecentData: UserMetricsParsedData[] = filteredHistoricData.filter((item) => {
    return monthSet.has(item.create_date_formatted);
  });

  return [filteredHistoricData, filteredRecentData];
};

export const applyDashboardFiltersUserMetrics = (items: UserMetricsParsedData[], filters: DashboardFilters): UserMetricsParsedData[] => {
  return items.filter((item: UserMetricsParsedData) => {
    return (
      filters.team_name.includes(item.team_name) &&
      findCommonElements3(filters.managers, item.manager_name_hierarchy_concat) &&
      item.application !== ALL_APPLICATIONS
    );
  });
};

export const getDashboardFiltersFromUserMetrics = (items: UserMetricsParsedData[]): DashboardFilters => {
  const dashboardTicketFilters: DashboardFilters = {
    team_name: [],
    managers: [],
    assignees: []
  };

  items.forEach((item: UserMetricsParsedData) => {
    if (!dashboardTicketFilters.team_name.includes(item.team_name)) {
      dashboardTicketFilters.team_name.push(item.team_name);
    }

    if (!dashboardTicketFilters.managers.includes(item.manager_name)) {
      dashboardTicketFilters.managers.push(item.manager_name);
    }
  });
  return dashboardTicketFilters;
};

export const createUserMetricsMasterFilterData = (items: UserMetricsParsedData[]): UserMetricsMasterFilters => {
  const userMetricsMasterFilterData: UserMetricsMasterFilters = {
    managers: [],
    team: [],
    application_type: [],
    application_name: [],
    month: [],
    user_login: [],
    user_manager: [],
    org_include: true
  };

  // Use Sets for O(1) uniqueness checks instead of O(n) array .includes()
  const managersSet = new Set<string>();
  const teamSet = new Set<string>();
  const applicationTypeSet = new Set<string>();
  const applicationNameSet = new Set<string>();
  const monthSet = new Set<string>();
  const userLoginSet = new Set<string>();
  const userManagerSet = new Set<string>();

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.application === MANAGER_ROSTER) {
      continue;
    }
    if (!managersSet.has(item.manager_name)) {
      managersSet.add(item.manager_name);
      userMetricsMasterFilterData.managers.push(item.manager_name);
    }
    if (!teamSet.has(item.team_name)) {
      teamSet.add(item.team_name);
      userMetricsMasterFilterData.team.push(item.team_name);
    }
    if (!applicationTypeSet.has(item.application_type)) {
      applicationTypeSet.add(item.application_type);
      userMetricsMasterFilterData.application_type.push(item.application_type);
    }
    if (item.application !== ALL_APPLICATIONS && !applicationNameSet.has(item.application)) {
      applicationNameSet.add(item.application);
      userMetricsMasterFilterData.application_name.push(item.application);
    }
    if (!monthSet.has(item.create_date_formatted)) {
      monthSet.add(item.create_date_formatted);
      userMetricsMasterFilterData.month.push(item.create_date_formatted);
    }
    if (!userLoginSet.has(item.user_login)) {
      userLoginSet.add(item.user_login);
      userMetricsMasterFilterData.user_login.push(item.user_login);
    }
    addUniqueManagersToFilterWithSet(
      item.user_manager_name_hierarchy,
      userMetricsMasterFilterData.user_manager,
      userManagerSet,
      item.user_login,
      item.user_manager_login_hierarchy
    );
  }
  return userMetricsMasterFilterData;
};

/**
 * Excel-style cross-filtering: for each filter dimension, compute available values by
 * applying all OTHER active filters (excluding that dimension's own filter).
 * This prevents impossible selections while still showing the user what they deselected.
 */
export const computeCrossFilteredOptions = (items: UserMetricsParsedData[], filters: UserMetricsMasterFilters): UserMetricsMasterFilters => {
  const teamSet = new Set(filters.team);
  const applicationTypeSet = new Set(filters.application_type);
  const applicationNameSet = new Set(filters.application_name);
  const userLoginSet = new Set(filters.user_login);
  const monthSet = new Set(filters.month);
  // Set-based hierarchy overlap — see applyFiltersUserMetrics. This path is hotter still:
  // both hierarchy checks run for every row on every filter interaction.
  const managersSet = new Set(filters.managers);
  const userManagerFilterSet = new Set(filters.user_manager);

  const result: UserMetricsMasterFilters = {
    managers: [],
    team: [],
    application_type: [],
    application_name: [],
    month: [],
    user_login: [],
    user_manager: [],
    org_include: filters.org_include
  };

  const sets = {
    managers: new Set<string>(),
    team: new Set<string>(),
    application_type: new Set<string>(),
    application_name: new Set<string>(),
    month: new Set<string>(),
    user_login: new Set<string>(),
    user_manager: new Set<string>()
  };

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.application === MANAGER_ROSTER) continue;

    const passesTeam = teamSet.has(item.team_name);
    const passesAppType = applicationTypeSet.has(item.application_type);
    const passesAppName = applicationNameSet.has(item.application);
    const passesUserLogin = userLoginSet.has(item.user_login);
    const passesMonth = monthSet.has(item.create_date_formatted);
    const passesManagers = hasCommonElementWithSet(managersSet, item.manager_name_hierarchy_concat);
    const passesUserManager = hasCommonElementWithSet(userManagerFilterSet, item.user_manager_name_hierarchy);

    // For each dimension, check if the row passes ALL OTHER filters
    // Team: exclude team filter
    if (passesAppType && passesAppName && passesUserLogin && passesMonth && passesManagers && passesUserManager) {
      if (!sets.team.has(item.team_name)) {
        sets.team.add(item.team_name);
        result.team.push(item.team_name);
      }
    }
    // Application Type: exclude app type filter
    if (passesTeam && passesAppName && passesUserLogin && passesMonth && passesManagers && passesUserManager) {
      if (!sets.application_type.has(item.application_type)) {
        sets.application_type.add(item.application_type);
        result.application_type.push(item.application_type);
      }
    }
    // Application Name: exclude app name filter
    if (passesTeam && passesAppType && passesUserLogin && passesMonth && passesManagers && passesUserManager) {
      if (item.application !== ALL_APPLICATIONS && !sets.application_name.has(item.application)) {
        sets.application_name.add(item.application);
        result.application_name.push(item.application);
      }
    }
    // User Login: exclude user login filter
    if (passesTeam && passesAppType && passesAppName && passesMonth && passesManagers && passesUserManager) {
      if (!sets.user_login.has(item.user_login)) {
        sets.user_login.add(item.user_login);
        result.user_login.push(item.user_login);
      }
    }
    // Month: exclude month filter
    if (passesTeam && passesAppType && passesAppName && passesUserLogin && passesManagers && passesUserManager) {
      if (!sets.month.has(item.create_date_formatted)) {
        sets.month.add(item.create_date_formatted);
        result.month.push(item.create_date_formatted);
      }
    }
    // Managers: exclude managers filter — only add the direct manager (index [0])
    // to match createUserMetricsMasterFilterData behaviour and avoid exposing
    // higher-level managers (degardm+) that should not appear in the dropdown.
    if (passesTeam && passesAppType && passesAppName && passesUserLogin && passesMonth && passesUserManager) {
      if (!sets.managers.has(item.manager_name)) {
        sets.managers.add(item.manager_name);
        result.managers.push(item.manager_name);
      }
    }

    // User Manager: exclude user manager filter
    if (passesTeam && passesAppType && passesAppName && passesUserLogin && passesMonth && passesManagers) {
      addUniqueManagersToFilterWithSet(
        item.user_manager_name_hierarchy,
        result.user_manager,
        sets.user_manager,
        item.user_login,
        item.user_manager_login_hierarchy
      );
    }
  }

  return result;
};

/**
 * Adds unique managers from a hierarchy array to the filter list using a Set for O(1) lookups,
 * excluding the user's own login.
 */
const addUniqueManagersToFilterWithSet = (
  hierarchy: string[],
  existingManagers: string[],
  managerSet: Set<string>,
  userLogin: string,
  hierarchy_login: string[]
): void => {
  for (let i = 0; i < hierarchy.length; i++) {
    if (hierarchy[i] && hierarchy_login[i] && hierarchy_login[i] !== userLogin && !managerSet.has(hierarchy[i])) {
      managerSet.add(hierarchy[i]);
      existingManagers.push(hierarchy[i]);
    }
  }
};

export const createNonFinTechOrgData = (items: UserMetricsParsedData[]): [UserMetricsParsedData[], UserMetricsParsedData[]] => {
  const finTechUserItems: UserMetricsParsedData[] = [];
  const nonFinTechUserItems: UserMetricsParsedData[] = [];
  items.forEach((item) => {
    if (item.user_manager_login_hierarchy.includes(DAS_FINTECH_ORG_MANAGER_ALIAS)) {
      finTechUserItems.push(item);
    } else {
      nonFinTechUserItems.push(item);
    }
  });
  return [finTechUserItems, nonFinTechUserItems];
};
